import type {
  BaseURL,
  ClientKey,
  ComputeMetricSample,
  ComputeServer,
  CreateStressTestRun,
  EntranceTestAction,
  EntranceTestResponse,
  EntranceEffectiveCapabilities,
  ModelRoute,
  OffPeakPolicy,
  OverviewResponse,
  RealtimeCall,
  RealtimeReadiness,
  StressTestRun,
  SystemOverviewResponse,
  TranslationCapabilities,
  Upstream,
  UpstreamCluster,
  UsageDetailResponse,
  UsageListResponse,
  Workload,
  WorkloadImage,
  WorkloadImageUploadSession,
  WorkloadLog,
  WorkloadRun
} from './types';
import type { AppBossIntegration, AppBossIntegrationDraft } from './appBossIntegration';
import { integrationPayload, publicIntegration, safeIntegrationErrorCode } from './appBossIntegration';
import { diagnosticQuery, publicDiagnosticPage, type DiagnosticFilters, type DiagnosticKind, type DiagnosticPage } from './integrationDiagnostics';
import { publicEntranceLifecycle, lifecyclePayload, type EntranceLifecycle, type EntranceLifecycleAction } from './entranceLifecycle';
import { publicIntegrationProbe } from './integrationProbe';
import { pairingTicketRequest, publicPairingTicket } from './appBossPairing';
import { enrollmentPayload, publicEnrollmentPreview, publicEnrollmentResult, type AppBossEnrollmentPreview, type AppBossEnrollmentSelection } from './entranceEnrollment';

export class AdminAPIError extends Error {
  constructor(message: string, readonly status: number, readonly code = '') { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}, redact = false): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {})
    },
    ...init
  });
  if (!res.ok) {
    const text = await res.text();
    let code = '';
    if (redact) { try { code = safeIntegrationErrorCode(JSON.parse(text)?.error?.code); } catch { /* Non-JSON errors must also stay private. */ } }
    throw new AdminAPIError(redact ? 'AppBoss integration request failed' : text || res.statusText, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const queryKeys = {
  effectiveCapabilities: (baseURLID: string) => ['effectiveCapabilities', baseURLID] as const
};

export const api = {
  shareAppBossSource: (childId:string,expectedRevision:number,sourceRevision:number) => request<unknown>(`/api/admin/base-urls/${encodeURIComponent(childId)}/appboss-source-routing`,{method:'PUT',body:JSON.stringify({expectedRevision,sourceRevision,confirm:true})},true),
  configureEntranceBillingLimits: (sourceId:string,expectedRevision:number,limits:import('./entranceEnrollment').EntranceBillingLimits) => request<unknown>(`/api/admin/base-urls/${encodeURIComponent(sourceId)}/appboss-billing-limits`,{method:'PUT',body:JSON.stringify({expectedRevision,limits})},true),
  createAppBossPairingTicket: (name: string, origin: string) => request<unknown>('/api/admin/appboss-pairing-tickets', { method: 'POST', body: JSON.stringify(pairingTicketRequest(name, origin)) }, true).then((value) => publicPairingTicket(value)),
  appBossEnrollment: (sourceId: string, signal?: AbortSignal) =>
    request<unknown>(`/api/admin/base-urls/${encodeURIComponent(sourceId)}/appboss-enrollment`, { signal }, true).then((result) => publicEnrollmentPreview(result, sourceId)),
  enrollAppBossEntrance: (view: AppBossEnrollmentPreview, selection: AppBossEnrollmentSelection) => {
    const sourceId = view.source.id;
    const body = enrollmentPayload(view, selection);
    return request<unknown>(`/api/admin/base-urls/${encodeURIComponent(sourceId)}/appboss-enrollment`, { method: 'POST', body: JSON.stringify(body) }, true).then((result) => publicEnrollmentResult(result, sourceId, body.integrationId));
  },
  testAppBossIntegration: (id: string, expectedRevision: number, signal?: AbortSignal) => request<unknown>(`/api/admin/appboss-integrations/${encodeURIComponent(id)}/test`, { method: 'POST', body: JSON.stringify({ expectedRevision }), signal }, true).then((result) => publicIntegrationProbe(result, id, expectedRevision)),
  entranceLifecycle: (id: string, signal?: AbortSignal) => request<unknown>(`/api/admin/base-urls/${encodeURIComponent(id)}/lifecycle`, { signal }, true).then(publicEntranceLifecycle),
  changeEntranceLifecycle: (view: EntranceLifecycle, action: EntranceLifecycleAction, reason: string, force: boolean, acknowledged: boolean) =>
    request<unknown>(`/api/admin/base-urls/${encodeURIComponent(view.entranceId)}/lifecycle`, { method: 'POST', body: JSON.stringify(lifecyclePayload(view, action, reason, force, acknowledged)) }, true).then(publicEntranceLifecycle),
  appBossDiagnostics: (kind: DiagnosticKind, filters: DiagnosticFilters, cursor?: string, signal?: AbortSignal) => request<DiagnosticPage>(`/api/admin/appboss-diagnostics?${diagnosticQuery(kind, filters, cursor)}`, { signal }, true).then(publicDiagnosticPage),
  appBossIntegrations: (signal?: AbortSignal) => request<{ instanceId: string; integrations: AppBossIntegration[] }>('/api/admin/appboss-integrations', { signal }, true)
    .then(({ instanceId, integrations }) => ({ instanceId, integrations: integrations.map(publicIntegration) })),
  appBossAudits: (filters: { actor: string; action: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams({ resource: 'appboss_integration' });
    if (filters.actor) query.set('actor', filters.actor);
    if (filters.action) query.set('action', filters.action);
    return request<import('./types').AuditLog[]>(`/api/admin/audits?${query}`, { signal }, true).then((rows) => rows.map(({ id, actor, action, resource, resourceId, createdAt }) => ({ id, actor, action, resource, resourceId, createdAt })));
  },
  saveAppBossIntegration: (draft: AppBossIntegrationDraft, existing?: AppBossIntegration | null, rotate = false) =>
    request<{ integration: AppBossIntegration; managementCredential?: string }>('/api/admin/appboss-integrations', { method: existing ? 'PUT' : 'POST', body: JSON.stringify(integrationPayload(draft, existing, rotate)) }, true)
      .then(({ integration, managementCredential }) => ({ integration: publicIntegration(integration), ...(typeof managementCredential === 'string' ? { managementCredential } : {}) })),
  login: (username: string, password: string) =>
    request<{ username: string; role: string }>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    }),
  logout: () => request('/api/admin/logout', { method: 'POST' }),
  me: () => request<{ username: string }>('/api/admin/me'),
  changeAdminPassword: (body: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    request<{ ok: boolean }>('/api/admin/me/password', { method: 'POST', body: JSON.stringify(body) }),
  overview: () => request<OverviewResponse>('/api/admin/overview'),
  realtimeCalls: () => request<RealtimeCall[]>('/api/admin/realtime/calls'),
  realtimeReadiness: () => request<RealtimeReadiness>('/api/admin/realtime/readiness'),
  systemOverview: (nodeUuid = '', range = '1h', resolution = '') => {
    const query = new URLSearchParams({ range });
    if (nodeUuid) query.set('nodeUuid', nodeUuid);
    if (resolution) query.set('resolution', resolution);
    return request<SystemOverviewResponse>(`/api/admin/system/overview?${query.toString()}`);
  },
  baseURLs: () => request<BaseURL[]>('/api/admin/base-urls'),
  createBaseURL: (body: Partial<BaseURL>) =>
    request<BaseURL>('/api/admin/base-urls', { method: 'POST', body: JSON.stringify(body) }, body.authMode === 'appboss_token'),
  updateBaseURL: (id: string, body: Partial<BaseURL>) =>
    request<BaseURL>(`/api/admin/base-urls/${id}`, { method: 'PUT', body: JSON.stringify(body) }, body.authMode === 'appboss_token'),
  deleteBaseURL: (id: string) => request(`/api/admin/base-urls/${id}`, { method: 'DELETE' }),
  testBaseURL: (id: string, body: { model?: string; question?: string; action: EntranceTestAction; sourceLang?: string; targetLang?: string }) =>
    request<EntranceTestResponse>(`/api/admin/base-urls/${id}/test`, { method: 'POST', body: JSON.stringify(body) }),
  effectiveCapabilities: (id: string, refresh = false) =>
    request<EntranceEffectiveCapabilities>(`/api/admin/base-urls/${id}/effective-capabilities${refresh ? '?refresh=true' : ''}`),
  rotateRealtimeWebhookSecret: (id: string) =>
    request<{ secret: string; baseUrl: BaseURL }>(`/api/admin/base-urls/${id}/realtime-webhook-secret`, { method: 'POST' }),
  keys: (baseUrlId: string) => request<ClientKey[]>(`/api/admin/base-urls/${baseUrlId}/keys`),
  createKey: (baseUrlId: string, name: string) =>
    request<{ key: ClientKey; secret: string }>(`/api/admin/base-urls/${baseUrlId}/keys`, {
      method: 'POST',
      body: JSON.stringify({ name })
    }),
  revokeKey: (baseUrlId: string, keyId: string) =>
    request(`/api/admin/base-urls/${baseUrlId}/keys/${keyId}`, { method: 'DELETE' }),
  rotateKey: (baseUrlId: string, keyId: string) =>
    request<{ key: ClientKey; secret: string }>(`/api/admin/base-urls/${baseUrlId}/keys/${keyId}/rotate`, { method: 'POST' }),
  upstreams: () => request<Upstream[]>('/api/admin/upstreams'),
  createUpstream: (body: Partial<Upstream>) =>
    request<Upstream>('/api/admin/upstreams', { method: 'POST', body: JSON.stringify(body) }),
  updateUpstream: (id: string, body: Partial<Upstream>) =>
    request<Upstream>(`/api/admin/upstreams/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteUpstream: (id: string) => request(`/api/admin/upstreams/${id}`, { method: 'DELETE' }),
  healthUpstream: (id: string) => request<any>(`/api/admin/upstreams/${id}/health`, { method: 'POST' }),
  rotateRealtimeWebhookReceiver: (id: string) =>
    request<Upstream>(`/api/admin/upstreams/${id}/realtime-webhook-receiver`, { method: 'POST' }),
  translationCapabilities: (id: string) =>
    request<TranslationCapabilities>(`/api/admin/upstreams/${id}/translation-capabilities`),
  upstreamClusters: () => request<UpstreamCluster[]>('/api/admin/upstream-clusters'),
  createUpstreamCluster: (body: Partial<UpstreamCluster>) =>
    request<UpstreamCluster>('/api/admin/upstream-clusters', { method: 'POST', body: JSON.stringify(body) }),
  updateUpstreamCluster: (id: string, body: Partial<UpstreamCluster>) =>
    request<UpstreamCluster>(`/api/admin/upstream-clusters/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteUpstreamCluster: (id: string) => request(`/api/admin/upstream-clusters/${id}`, { method: 'DELETE' }),
  healthUpstreamCluster: (id: string) => request<any>(`/api/admin/upstream-clusters/${id}/health`, { method: 'POST' }),
  addUpstreamClusterMember: (clusterId: string, upstreamId: string) =>
    request<UpstreamCluster>(`/api/admin/upstream-clusters/${clusterId}/members`, { method: 'POST', body: JSON.stringify({ upstreamId }) }),
  updateUpstreamClusterMember: (clusterId: string, upstreamId: string, status: string) =>
    request<UpstreamCluster>(`/api/admin/upstream-clusters/${clusterId}/members/${upstreamId}`, { method: 'PUT', body: JSON.stringify({ status }) }),
  removeUpstreamClusterMember: (clusterId: string, upstreamId: string) =>
    request<UpstreamCluster>(`/api/admin/upstream-clusters/${clusterId}/members/${upstreamId}`, { method: 'DELETE' }),
  reorderUpstreamClusterMembers: (clusterId: string, upstreamIds: string[]) =>
    request<UpstreamCluster>(`/api/admin/upstream-clusters/${clusterId}/members/reorder`, { method: 'PUT', body: JSON.stringify({ upstreamIds }) }),
  modelRoutes: (baseUrlId?: string) =>
    request<ModelRoute[]>(`/api/admin/model-routes${baseUrlId ? `?baseUrlId=${baseUrlId}` : ''}`),
  createModelRoute: (body: Partial<ModelRoute>) =>
    request<ModelRoute>('/api/admin/model-routes', { method: 'POST', body: JSON.stringify(body) }),
  updateModelRoute: (id: string, body: Partial<ModelRoute>) =>
    request<ModelRoute>(`/api/admin/model-routes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteModelRoute: (id: string) => request(`/api/admin/model-routes/${id}`, { method: 'DELETE' }),
  usage: (query = '') => request<UsageListResponse>(`/api/admin/usage${query}`, {}, true),
  usageDetail: (id: string) => request<UsageDetailResponse>(`/api/admin/usage/${id}/detail`, {}, true),
  audits: () => request<any[]>('/api/admin/audits'),
  stressTests: () => request<StressTestRun[]>('/api/admin/stress-tests'),
  stressTest: (id: string) => request<StressTestRun>(`/api/admin/stress-tests/${id}`),
  createStressTest: (body: CreateStressTestRun) =>
    request<StressTestRun>('/api/admin/stress-tests', { method: 'POST', body: JSON.stringify(body) }),
  cancelStressTest: (id: string) => request<StressTestRun>(`/api/admin/stress-tests/${id}/cancel`, { method: 'POST' }),
  resetCounters: () => request<any>('/api/admin/settings/reset-counters', { method: 'POST' }),
  resetLogs: () => request<any>('/api/admin/settings/reset-logs', { method: 'POST' }),
  computeServers: () => request<ComputeServer[]>('/api/admin/compute/servers'),
  computeHistory: (uuid: string, from: string, to: string) =>
    request<{ serverUuid: string; from: string; to: string; samples: ComputeMetricSample[] }>(
      `/api/admin/compute/servers/${encodeURIComponent(uuid)}/history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    ),
  holdComputeServer: (uuid: string, body: { hold: boolean; reason?: string }) =>
    request(`/api/admin/compute/servers/${encodeURIComponent(uuid)}/hold`, { method: 'POST', body: JSON.stringify(body) }),
  restoreComputeServer: (uuid: string) => request(`/api/admin/compute/servers/${encodeURIComponent(uuid)}/restore`, { method: 'POST' }),
  offPeakPolicies: () => request<OffPeakPolicy[]>('/api/admin/off-peak/policies'),
  createOffPeakPolicy: (body: Partial<OffPeakPolicy>) =>
    request<OffPeakPolicy>('/api/admin/off-peak/policies', { method: 'POST', body: JSON.stringify(body) }),
  updateOffPeakPolicy: (id: string, body: Partial<OffPeakPolicy>) =>
    request<OffPeakPolicy>(`/api/admin/off-peak/policies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOffPeakPolicy: (id: string) => request(`/api/admin/off-peak/policies/${id}`, { method: 'DELETE' }),
  enableOffPeakPolicy: (id: string) => request<OffPeakPolicy>(`/api/admin/off-peak/policies/${id}/enable`, { method: 'POST' }),
  disableOffPeakPolicy: (id: string) => request<OffPeakPolicy>(`/api/admin/off-peak/policies/${id}/disable`, { method: 'POST' }),
  workloadImages: () => request<WorkloadImage[]>('/api/admin/workload-images'),
  createWorkloadImage: (body: Partial<WorkloadImage>) =>
    request<{ image: WorkloadImage; uploadSession: WorkloadImageUploadSession }>('/api/admin/workload-images', { method: 'POST', body: JSON.stringify(body) }),
  deleteWorkloadImage: (id: string) => request(`/api/admin/workload-images/${id}`, { method: 'DELETE' }),
  uploadSession: (id: string) => request<{ session: WorkloadImageUploadSession; chunks: any[] }>(`/api/admin/workload-images/upload-sessions/${id}`),
  uploadImageChunk: async (sessionId: string, index: number, chunk: Blob) => {
    const res = await fetch(`/api/admin/workload-images/upload-sessions/${sessionId}/chunks/${index}`, {
      method: 'PUT',
      credentials: 'include',
      body: chunk
    });
    if (!res.ok) throw new Error((await res.text()) || res.statusText);
    return res.json();
  },
  completeImageUpload: (sessionId: string) => request<WorkloadImage>(`/api/admin/workload-images/upload-sessions/${sessionId}/complete`, { method: 'POST' }),
  workloads: () => request<Workload[]>('/api/admin/workloads'),
  createWorkload: (body: Partial<Workload>) => request<Workload>('/api/admin/workloads', { method: 'POST', body: JSON.stringify(body) }),
  updateWorkload: (id: string, body: Partial<Workload>) => request<Workload>(`/api/admin/workloads/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteWorkload: (id: string) => request(`/api/admin/workloads/${id}`, { method: 'DELETE' }),
  enableWorkload: (id: string) => request<Workload>(`/api/admin/workloads/${id}/enable`, { method: 'POST' }),
  disableWorkload: (id: string) => request<Workload>(`/api/admin/workloads/${id}/disable`, { method: 'POST' }),
  workloadRuns: () => request<WorkloadRun[]>('/api/admin/workload-runs'),
  startWorkloadRun: (body: { workloadId: string; nodeUuids: string[] }) => request<{ runs: WorkloadRun[] }>('/api/admin/workload-runs', { method: 'POST', body: JSON.stringify(body) }),
  stopWorkloadRun: (id: string) => request(`/api/admin/workload-runs/${id}/stop`, { method: 'POST' }),
  restoreWorkloadRun: (id: string) => request(`/api/admin/workload-runs/${id}/restore`, { method: 'POST' }),
  workloadLogs: (query = '') => request<WorkloadLog[]>(`/api/admin/workload-logs${query}`)
};
